import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useBankAccountsByBank } from "Api/Queries/Accounts/useBankAccountsByBank";
import { bankingAccountsKeys } from "Api/Queries/Accounts/useBankingAccounts";
import { orbitLStatusKeys } from "Api/Queries/Merchants/useGetOrbitLStatusQuery";
import { useRemoveBankAccounts } from "Api/Mutations/Accounts/useRemoveBankAccounts";
import { Resources, useResource } from "Translations/Resources";
import type {
  BankAccountListItem,
  BankEnvironment,
} from "Infrastructure/Api/Api";
import { toast } from "sonner";
import { useSearchParams } from "Navigation/useSearchParams";

type UseBankConnectionSuccessHandlerReturn = {
  shouldShowDialog: boolean;
  isDialogOpen: boolean;
  bankAccounts?: Array<BankAccountListItem>;
  bankName: string;
  isLoading: boolean;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (selectedAccountIds: Array<number>) => void;
};

export const useBankConnectionSuccessHandler =
  (): UseBankConnectionSuccessHandlerReturn => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [bankEnvironment, setBankEnvironment] =
      useState<BankEnvironment | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { t } = useResource();

    const { data: bankAccountsResponse, isLoading } =
      useBankAccountsByBank(bankEnvironment);

    const bankAccounts = bankAccountsResponse?.items;
    const { mutate: removeAccounts, isPending } = useRemoveBankAccounts();

    useEffect(() => {
      const connectedBankEnvironment = searchParams.get(
        "connectedBankEnvironment",
      ) as BankEnvironment;

      if (connectedBankEnvironment) {
        setBankEnvironment(connectedBankEnvironment);
        setIsDialogOpen(true);

        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("connectedBankEnvironment");
        setSearchParams(newSearchParams, { replace: true });
      }
    }, [searchParams, setSearchParams]);

    const handleDialogClose = () => {
      setIsDialogOpen(false);
      setBankEnvironment(null);

      // The return from the bank is a full page load, so the first read is fresh anyway — what it
      // cannot cover is the primary account changing here in the dialog. The Orbit L checklist reads
      // that account, so it has to be refetched once the selection is settled.
      queryClient.invalidateQueries({ queryKey: bankingAccountsKeys.all });
      queryClient.invalidateQueries({ queryKey: orbitLStatusKeys.all });
    };

    const handleConfirm = (selectedAccountIds: Array<number>) => {
      if (!bankAccounts) {
        return;
      }

      const accountsToRemove = bankAccounts
        .filter(
          ({ bankAccountID }) => !selectedAccountIds.includes(bankAccountID),
        )
        .map(({ bankAccountID }) => bankAccountID);

      if (accountsToRemove.length > 0) {
        removeAccounts(accountsToRemove, {
          onSuccess: () => {
            toast.success(t(Resources.Banking.AccountSelection.AccountsSetUp));
            handleDialogClose();
          },
          onError: () => {
            toast.error(t(Resources.Banking.AccountSelection.RemoveError));
          },
        });
      } else {
        handleDialogClose();
      }
    };

    const bankName = bankEnvironment
      ? t(Resources.BankEnvironmentNames[bankEnvironment])
      : "";

    const shouldShowDialog =
      isDialogOpen && !!bankAccounts && bankAccounts.length > 1;

    return {
      shouldShowDialog,
      isDialogOpen,
      bankAccounts,
      bankName,
      isLoading,
      isPending,
      onClose: handleDialogClose,
      onConfirm: handleConfirm,
    };
  };
